import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { API_BASE } from '../config';
const API = API_BASE;

function readUser() {
  try { return JSON.parse(localStorage.getItem('guruone_user')) || {}; } catch { return {}; }
}

function authHeaders() {
  const token = localStorage.getItem('guruone_token') || '';
  return { Authorization: `Bearer ${token}` };
}

export default function TwoFactorSetup() {
  const navigate = useNavigate();
  // Snapshot totp_enabled ONCE at mount. We deliberately don't recompute
  // from localStorage on every render: handleActivate() updates the
  // localStorage user object to totp_enabled=true so other pages see
  // the new state, but if we read it here reactively the page would
  // flip straight to the "2FA is active" / Disable view and skip the
  // backup-codes screen the user must see.
  const [totpEnabled] = useState(() => {
    const u = readUser();
    return !!u.totp_enabled;
  });
  // Tab state — '2fa' (the management surface) or 'about' (security explainer)
  const [tab, setTab] = useState('2fa');

  // Setup-flow state
  // step: 'idle' (not yet generated) | 'qr' (QR shown, code pending) | 'activated' (backup codes shown)
  const [step, setStep] = useState('idle');
  const [qrCode, setQrCode] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [copied, setCopied] = useState(false);

  // Disable-flow state
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState('');

  // Shared
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleGenerateQR() {
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/setup`, {}, { headers: authHeaders() });
      setQrCode(res.data?.qrCode || '');
      setSecret(res.data?.secret || '');
      setCode('');
      setStep('qr');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start 2FA setup. Please try again.');
    }
    setLoading(false);
  }

  async function handleActivate(e) {
    if (e && e.preventDefault) e.preventDefault();
    setError('');
    if (code.length !== 6) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/verify-setup`, { token: code }, { headers: authHeaders() });
      const codes = Array.isArray(res.data?.backupCodes) ? res.data.backupCodes : [];
      setBackupCodes(codes);
      // Update local user so the UI reflects the enabled state on return.
      try {
        const u = readUser();
        localStorage.setItem('guruone_user', JSON.stringify({ ...u, totp_enabled: true }));
      } catch { /* non-fatal */ }
      setStep('activated');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not activate 2FA. Check the code and try again.');
    }
    setLoading(false);
  }

  async function handleCopyBackupCodes() {
    if (!backupCodes.length) return;
    try {
      await navigator.clipboard.writeText(backupCodes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setError('Could not copy. Select the codes manually instead.');
    }
  }

  async function handleDisable(e) {
    if (e && e.preventDefault) e.preventDefault();
    setError('');
    if (!password) { setError('Enter your password to confirm.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { password }, { headers: authHeaders() });
      try {
        const u = readUser();
        localStorage.setItem('guruone_user', JSON.stringify({ ...u, totp_enabled: false }));
      } catch { /* non-fatal */ }
      setPassword('');
      setConfirming(false);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not disable 2FA. Check your password and try again.');
    }
    setLoading(false);
  }

  const inputStyle = {
    width: '100%', padding: '0.75rem 0.9rem', borderRadius: 8,
    border: '1.5px solid #e5e7eb', fontSize: '0.9rem', fontFamily: 'inherit',
    boxSizing: 'border-box', outline: 'none', transition: 'border-color 0.15s',
  };

  const primaryBtn = {
    padding: '0.85rem 1.25rem', borderRadius: 8, background: '#1D9E75', color: 'white',
    border: 'none', fontSize: '0.92rem', fontWeight: 600, cursor: 'pointer',
    fontFamily: 'inherit', transition: 'opacity 0.15s', width: 'auto',
  };

  const secondaryBtn = {
    padding: '0.7rem 1rem', borderRadius: 8, background: 'white', color: '#555',
    border: '1px solid #d1d5db', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
    fontFamily: 'inherit', width: 'auto',
  };

  const dangerBtn = {
    padding: '0.85rem 1.25rem', borderRadius: 8, background: 'white', color: '#991B1B',
    border: '1.5px solid #FECACA', fontSize: '0.92rem', fontWeight: 600, cursor: 'pointer',
    fontFamily: 'inherit', width: 'auto',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f7f7f9' }}>
      <Header />
      <main style={{ maxWidth: 720, margin: '0 auto', padding: '2rem 1.5rem' }}>
        <button
          onClick={() => navigate('/dashboard')}
          style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.78rem', cursor: 'pointer', padding: 0, marginBottom: '1rem', fontFamily: 'inherit' }}>
          ← Back to dashboard
        </button>

        <div style={{ background: 'white', borderRadius: 14, padding: '2rem', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: '0.25rem', borderBottom: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
            {[
              { key: '2fa',   label: 'Two-factor authentication' },
              { key: 'about', label: 'About our security' },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                style={{
                  background: 'none',
                  border: 'none',
                  borderBottom: tab === t.key ? '2px solid #1D9E75' : '2px solid transparent',
                  marginBottom: -1,
                  padding: '0.65rem 0.85rem',
                  color: tab === t.key ? '#1a1a2e' : '#888',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  width: 'auto',
                  whiteSpace: 'nowrap',
                  transition: 'color 0.15s',
                }}>
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'about' && (
            <div style={{ lineHeight: 1.7, color: '#1a1a2e' }}>
              <h1 style={{ margin: '0 0 1rem', fontSize: '1.35rem', fontWeight: 700 }}>How GuruOne keeps your data safe</h1>
              <p style={{ fontSize: '0.92rem', color: '#1a1a2e', margin: '0 0 1rem' }}>
                <strong>Built on banking-grade primitives.</strong> GuruOne&apos;s authentication uses the same primitives as your online banking and your accounting software — TLS-encrypted connections, bcrypt one-way password hashing (so even GuruOne staff never see your plain password), short-lived signed sessions, and optional TOTP two-factor authentication. The same approach a bank uses to protect a high-value transaction is what protects your account here.
              </p>
              <p style={{ fontSize: '0.92rem', color: '#1a1a2e', margin: '0 0 1rem' }}>
                <strong>Your strategic data stays yours.</strong> Your canvases, decisions and conversations are stored in an encrypted database and used only to ground <em>your</em> future GuruOne recommendations. We do not sell your data, share it for marketing, or feed it into models that other companies can access.
              </p>
              <p style={{ fontSize: '0.92rem', color: '#1a1a2e', margin: 0 }}>
                <strong>Working with an LLM.</strong> Recommendations are generated via Anthropic&apos;s Claude API. Per Anthropic&apos;s API terms, your prompts are not used to train models and are deleted within 30 days. We send only the minimum needed for the question you&apos;re asking — your business profile, the relevant canvas section, and your specific challenge — never more than that.
              </p>
            </div>
          )}

          {tab === '2fa' && (totpEnabled ? (
            // ── DISABLE FLOW ─────────────────────────────────────────
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '0.75rem' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#1D9E75', display: 'inline-block' }} />
                <h1 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 700, color: '#1a1a2e' }}>
                  Two-Factor Authentication is active
                </h1>
              </div>
              <p style={{ fontSize: '0.88rem', color: '#555', lineHeight: 1.6, margin: '0 0 1.5rem' }}>
                Your account is protected by an authenticator app. You'll be asked for a 6-digit code each time you sign in.
              </p>

              {!confirming && (
                <button onClick={() => { setConfirming(true); setError(''); }} style={dangerBtn}>
                  Disable 2FA
                </button>
              )}

              {confirming && (
                <form onSubmit={handleDisable} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: 360 }}>
                  <p style={{ fontSize: '0.85rem', color: '#555', margin: 0, lineHeight: 1.55 }}>
                    Enter your password to confirm. This will remove 2FA from your account.
                  </p>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>Password</label>
                    <input
                      type="password" value={password} onChange={e => setPassword(e.target.value)}
                      required autoComplete="current-password" style={inputStyle}
                    />
                  </div>
                  {error && (
                    <div style={{ background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#991B1B' }}>
                      {error}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button type="submit" disabled={loading || !password} style={{ ...dangerBtn, background: '#991B1B', color: 'white', borderColor: '#991B1B', opacity: loading || !password ? 0.6 : 1 }}>
                      {loading ? 'Disabling...' : 'Confirm disable'}
                    </button>
                    <button type="button" onClick={() => { setConfirming(false); setPassword(''); setError(''); }} style={secondaryBtn}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </>
          ) : (
            // ── SETUP FLOW ───────────────────────────────────────────
            <>
              {step !== 'activated' && (
                <>
                  <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.35rem', fontWeight: 700, color: '#1a1a2e' }}>
                    Set up Two-Factor Authentication
                  </h1>
                  <p style={{ fontSize: '0.88rem', color: '#555', lineHeight: 1.6, margin: '0 0 1.5rem' }}>
                    Use an authenticator app like Google Authenticator, Microsoft Authenticator, or Authy. Scan the QR code below, then enter the 6-digit code to confirm.
                  </p>
                </>
              )}

              {step === 'idle' && (
                <button onClick={handleGenerateQR} disabled={loading} style={{ ...primaryBtn, opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}>
                  {loading ? 'Generating...' : 'Generate QR code →'}
                </button>
              )}

              {step === 'qr' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem', background: '#f7f7f9', borderRadius: 10 }}>
                    {qrCode
                      ? <img src={qrCode} alt="2FA QR code" style={{ width: 220, height: 220, display: 'block' }} />
                      : <div style={{ width: 220, height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: '0.8rem' }}>QR code unavailable</div>}
                  </div>

                  <form onSubmit={handleActivate} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>
                        6-digit code from your authenticator app
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        value={code}
                        onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        required
                        autoFocus
                        placeholder="123456"
                        autoComplete="one-time-code"
                        style={{ ...inputStyle, letterSpacing: '0.4em', textAlign: 'center', fontSize: '1.05rem', fontVariantNumeric: 'tabular-nums', maxWidth: 240 }}
                      />
                    </div>
                    {error && (
                      <div style={{ background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#991B1B' }}>
                        {error}
                        {/^invalid code/i.test(error) && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: '#7c2d2d', lineHeight: 1.55 }}>
                            <strong>Have you set up 2FA on this account before?</strong> If you previously enabled and then disabled 2FA, your authenticator app may still hold the old GuruOne entry — codes from that old entry will not work. Delete the old GuruOne entry from your authenticator, then scan the QR code above and enter a fresh code from the new entry.
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <button type="submit" disabled={loading || code.length !== 6} style={{ ...primaryBtn, opacity: (loading || code.length !== 6) ? 0.6 : 1, cursor: (loading || code.length !== 6) ? 'not-allowed' : 'pointer' }}>
                        {loading ? 'Activating...' : 'Activate 2FA →'}
                      </button>
                      <button type="button" onClick={() => { setStep('idle'); setQrCode(''); setSecret(''); setCode(''); setError(''); }} style={secondaryBtn}>
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {step === 'activated' && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '0.75rem' }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#1D9E75', display: 'inline-block' }} />
                    <h1 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 700, color: '#1a1a2e' }}>
                      2FA is active
                    </h1>
                  </div>
                  <p style={{ fontSize: '0.88rem', color: '#555', lineHeight: 1.6, margin: '0 0 1rem' }}>
                    Store these somewhere safe — you can use them if you lose access to your app. Each code works once.
                  </p>

                  {backupCodes.length > 0 ? (
                    <>
                      <div style={{ background: '#f7f7f9', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1rem' }}>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem 1.25rem' }}>
                          {backupCodes.map((bc, i) => (
                            <li key={i} style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.92rem', color: '#1a1a2e', letterSpacing: '0.05em' }}>
                              {bc}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem' }}>
                        <button onClick={handleCopyBackupCodes} style={secondaryBtn}>
                          {copied ? '✓ Copied' : 'Copy all'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <p style={{ fontSize: '0.85rem', color: '#888', margin: '0 0 1.25rem', fontStyle: 'italic' }}>
                      No backup codes were returned. Contact support before logging out.
                    </p>
                  )}

                  <button onClick={() => navigate('/dashboard')} style={primaryBtn}>
                    Done →
                  </button>
                </>
              )}

              {step === 'idle' && error && (
                <div style={{ marginTop: '1rem', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#991B1B' }}>
                  {error}
                </div>
              )}
            </>
          ))}
        </div>
      </main>
    </div>
  );
}
